/* eslint-disable react/forbid-dom-props */
import {PhotoCheckerMarkerIcon} from './PhotoCheckerMarkerIcon';
import type {PhotoCheckerPartVariants} from './types/PhotoCheckerPartVariants';
import type {PhotoCheckerSilhouettePart} from './types/PhotoCheckerSilhouettePart';
import type {PhotoCheckerSilhouetteVariantColors} from './types/PhotoCheckerSilhouetteVariantColors';

interface PhotoCheckerSilhouetteOverlayProps {
  colorsByVariant: PhotoCheckerSilhouetteVariantColors;
  partVariants: PhotoCheckerPartVariants;
}

interface PhotoCheckerGradientDefinition {
  part: PhotoCheckerSilhouettePart;
  id: string;
  pathD: string;
  x1: number | string;
  y1: number | string;
  x2: number | string;
  y2: number | string;
}

interface PhotoCheckerMarkerDefinition {
  part: PhotoCheckerSilhouettePart;
  circle: {
    x?: number;
    y?: number;
    width: number;
    height: number;
    rx: number;
  };
  mask: {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rect: {
      x: number;
      y: number;
      width: number;
      height: number;
      transform?: string;
    };
  };
  icon: {
    x: number;
    y: number;
    size: number;
  };
}

const photoCheckerGradientDefinitions = [
  {
    part: 'frontLeft',
    id: 'paint0_linear_29783_11095',
    pathD:
      'M100.368 203.255L51.5078 216.62L47.0711 212.184L42.6343 207.747L55.1127 158L77.7401 180.627L100.368 203.255Z',
    x1: 46.6274,
    y1: 211.74,
    x2: 77.7401,
    y2: 180.627,
  },
  {
    part: 'frontRight',
    id: 'paint1_linear_29783_11095',
    pathD:
      'M100.368 43.1127L51.5078 29.747L47.0711 34.1837L42.6343 38.6205L55.1127 88.3675L77.7401 65.7401L100.368 43.1127Z',
    x1: 46.6274,
    y1: 34.6274,
    x2: 77.7401,
    y2: 65.7401,
  },
  {
    part: 'rearLeft',
    id: 'paint2_linear_29783_11095',
    pathD:
      'M344 203.255L392.86 216.62L397.296 212.184L401.733 207.747L389.255 158L366.627 180.627L344 203.255Z',
    x1: 397.74,
    y1: 211.74,
    x2: 366.627,
    y2: 180.627,
  },
  {
    part: 'rear',
    id: 'paint3_linear_29783_11095',
    pathD: 'M389 91L433 116.098L433 122.373L433 128.647L389 155L389 123L389 91Z',
    x1: 433,
    y1: 123,
    x2: 389,
    y2: 123,
  },
  {
    part: 'rearRight',
    id: 'paint4_linear_29783_11095',
    pathD:
      'M344 43.1127L392.86 29.747L397.296 34.1837L401.733 38.6205L389.255 88.3675L366.627 65.7401L344 43.1127Z',
    x1: 397.74,
    y1: 34.6274,
    x2: 366.627,
    y2: 65.7401,
  },
  {
    part: 'front',
    id: 'paint5_linear_29783_11095',
    pathD: 'M58 91L14 116.098L14 122.373L14 128.647L58 155L58 123L58 91Z',
    x1: 14,
    y1: 123,
    x2: 58,
    y2: 123,
  },
  {
    part: 'leftSide',
    id: 'paint6_linear_29783_11095',
    pathD: 'M192 184L217.098 228H223.373H229.647L256 184H224H192Z',
    x1: 224,
    y1: 228,
    x2: 224,
    y2: 184,
  },
  {
    part: 'rightSide',
    id: 'paint7_linear_29783_11095',
    pathD: 'M192 60L217.098 16H223.373H229.647L256 60H224H192Z',
    x1: 224,
    y1: 16,
    x2: 224,
    y2: 60,
  },
] satisfies PhotoCheckerGradientDefinition[];

const photoCheckerMarkerDefinitions = [
  createMarkerDefinition('rearRight', 'mask0_29783_11095', 389, 20, 24, 391, 22, 20),
  createMarkerDefinition('frontRight', 'mask1_29783_11095', 31, 19, 24, 33, 21, 20),
  createMarkerDefinition('front', 'mask2_29783_11095', undefined, 111, 24, 2, 113, 20),
  createMarkerDefinition('frontLeft', 'mask3_29783_11095', 31, 203, 24, 33, 205, 20),
  createMarkerDefinition('rearLeft', 'mask4_29783_11095', 389, 203, 24, 391, 205, 20),
  createMarkerDefinition(
    'steeringWheel',
    'mask5_29783_11095',
    161,
    140,
    16,
    162.333,
    141.333,
    13.333
  ),
  createMarkerDefinition('frontSeats', 'mask6_29783_11095', 209, 111, 24, 211, 113, 20),
  createMarkerDefinition('backSeats', 'mask7_29783_11095', 273, 111, 24, 275, 113, 20),
  createMarkerDefinition('trunk', 'mask8_29783_11095', 350, 111, 24, 352, 113, 20),
  createMarkerDefinition('rear', 'mask9_29783_11095', 423, 111, 24, 425, 113, 20),
  createMarkerDefinition(
    'infotainment',
    'mask10_29783_11095',
    160,
    115,
    16,
    161.333,
    116.333,
    13.333
  ),
  createMarkerDefinition(
    'dashboardTop',
    'mask11_29783_11095',
    160,
    88,
    16,
    161.333,
    89.333,
    13.333,
    {
      x: 174.667,
      y: 102.667,
      width: 13.3333,
      height: 13.3333,
      transform: 'rotate(180 174.667 102.667)',
    }
  ),
  createMarkerDefinition('leftSide', 'mask12_29783_11095', 212, 221, 24, 214, 223, 20, {
    x: 234,
    y: 243,
    width: 20,
    height: 20,
    transform: 'rotate(180 234 243)',
  }),
  createMarkerDefinition('rightSide', 'mask13_29783_11095', 212, undefined, 24, 214, 2, 20),
] satisfies PhotoCheckerMarkerDefinition[];

export function PhotoCheckerSilhouetteOverlay(props: PhotoCheckerSilhouetteOverlayProps) {
  return (
    <>
      {photoCheckerGradientDefinitions.map((gradient) => (
        <path
          key={gradient.id}
          d={gradient.pathD}
          fill={`url(#${gradient.id})`}
          fillOpacity="0.7"
        />
      ))}
      {photoCheckerMarkerDefinitions.map((marker) => (
        <g key={marker.mask.id}>
          <rect
            {...marker.circle}
            fill={props.colorsByVariant[props.partVariants[marker.part]].circle}
          />
          <mask
            id={marker.mask.id}
            style={{maskType: 'alpha'}}
            maskUnits="userSpaceOnUse"
            x={marker.mask.x}
            y={marker.mask.y}
            width={marker.mask.width}
            height={marker.mask.height}
          >
            <rect {...marker.mask.rect} fill="#D9D9D9" />
          </mask>
          <g mask={`url(#${marker.mask.id})`}>
            <PhotoCheckerMarkerIcon
              variant={props.partVariants[marker.part]}
              x={marker.icon.x}
              y={marker.icon.y}
              size={marker.icon.size}
            />
          </g>
        </g>
      ))}
    </>
  );
}

export function PhotoCheckerSilhouetteGradientDefs(props: PhotoCheckerSilhouetteOverlayProps) {
  return (
    <>
      {photoCheckerGradientDefinitions.map((gradient) => (
        <linearGradient
          key={gradient.id}
          id={gradient.id}
          x1={gradient.x1}
          y1={gradient.y1}
          x2={gradient.x2}
          y2={gradient.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={props.colorsByVariant[props.partVariants[gradient.part]].gradient} />
          <stop
            offset="1"
            stopColor={props.colorsByVariant[props.partVariants[gradient.part]].gradient}
            stopOpacity="0"
          />
        </linearGradient>
      ))}
    </>
  );
}

function createMarkerDefinition(
  part: PhotoCheckerSilhouettePart,
  maskId: string,
  circleX: number | undefined,
  circleY: number | undefined,
  circleSize: number,
  iconX: number,
  iconY: number,
  iconSize: number,
  maskRect?: PhotoCheckerMarkerDefinition['mask']['rect']
): PhotoCheckerMarkerDefinition {
  const rectSize = iconSize < 20 ? 13.3333 : iconSize;
  const maskRectOrDefault = maskRect ?? {
    x: iconX,
    y: iconY,
    width: rectSize,
    height: rectSize,
  };

  return {
    part,
    circle: {
      x: circleX,
      y: circleY,
      width: circleSize,
      height: circleSize,
      rx: circleSize / 2,
    },
    mask: {
      id: maskId,
      x: Math.trunc(iconX),
      y: Math.trunc(iconY),
      width: Math.ceil(rectSize),
      height: Math.ceil(rectSize),
      rect: maskRectOrDefault,
    },
    icon: {
      x: iconX,
      y: iconY,
      size: iconSize,
    },
  };
}
